import {Breadcrumb, Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import {useCallback, useEffect, useState} from 'react'
import moment from 'moment'
import {toast} from 'react-toastify'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_PIPELINES} from '../../gql/queries/pipelines'
import {IResPipelines} from '../../types/pipelines'
import {parseId} from '../../helpers'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalAddPipeline} from './components/ModalAddPipeline'
import {ModalDuplicatePipeline} from './components/ModalDuplicatePipeline'
import {ModalDeletePipeline} from './components/ModalDeletePipeline'
import {cleanVariables, PaginationUtils} from '../shared/PaginationUtils'
import {SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationSection} from '../shared/PaginationSection'
import {Link} from 'react-router-dom'
import {GET_ALL_ORGANIZATIONS} from '../../gql/queries/organizationsQuery'
import {useGetUser} from '../../store/userStore'
import {useQuery} from '@apollo/client'

export const PipelinesPage = () => {
  const [gqlGetPipelines, {data, error, refetch}] = useLazyQuery<IResPipelines>(
    GET_ALL_PIPELINES,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [dataArr, setDataArr] = useState<IResPipelines['allPipelines']['edges']>([])

  const [infoEdit, setInfoEdit] = useState<null | undefined | {id: string; name: string; organization?: {id: string; name: string}}>(null)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const [organizationId, setOrganizationId] = useState<string>('')

  const {isOpen, onClose, onOpen} = useDisclourse()
  const {
    isOpen: isOpenDuplicate,
    onClose: onCloseDuplicate,
    onOpen: onOpenDuplicate,
  } = useDisclourse()
  const {
    isOpen: isOpenDeletePipe,
    onClose: onCloseDeletePipe,
    onOpen: onOpenDeletePipe,
  } = useDisclourse()

  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [currentPage, setCurrentPage] = useState(1)

  let totalElements = data?.allPipelines?.totalCount ?? 0
  const totalPages = Math.ceil(totalElements / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalElements)

  const [formData, setFormData] = useState({
    text: '',
  })

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleFilters = useCallback(() => {
    const variables = cleanVariables({
      first,
      offset: (currentPage - 1) * first,
      nameIcontains: formData.text,
      organization: isSuperAdmin && organizationId ? parseId(organizationId) : null,
    })

    gqlGetPipelines({variables})
  }, [currentPage, first, formData.text, gqlGetPipelines, isSuperAdmin, organizationId])

  useEffect(() => {
    handleFilters()
  }, [handleFilters])

  useEffect(() => {
    if (!data) return
    const {edges} = data.allPipelines
    setDataArr(edges)
  }, [data])

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error.message}`)
    }
  }, [error])

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  useEffect(() => {
    if (errorOrganizations) toast.error(`Error: ${errorOrganizations.message}`)
  }, [errorOrganizations])

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none'>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Lead Pipelines</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          <Button
            size='sm'
            onClick={() => {
              onOpen()
              setInfoEdit(null)
            }}
          >
            <i className='bi bi-plus fs-3' /> New Pipeline
          </Button>
        </div>
      </header>
      <Row className='policies'>
        <Col md={12} lg={12} sm={12} xl={12} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>All Pipelines</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Search by pipeline name...'
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      text: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleFilters()
                    }
                  }}
                  value={formData.text}
                />
                {isSuperAdmin && (
                  <Form.Select
                    value={organizationId}
                    onChange={(e) => {
                      setOrganizationId(e.target.value)
                      setCurrentPage(1)
                    }}
                    disabled={loadingOrganizations}
                    style={{minHeight: '38px'}}
                  >
                    {loadingOrganizations ? (
                      <option value=''>Loading...</option>
                    ) : (
                      <option value=''>All Organizations</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Search
                </Button>
              </InputGroup>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setCurrentPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalElements}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
            </div>
            <div className='list-carrier table-scrollable table-container'>
              <table className='table-scrollable table-scroll table table-striped table-hover'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>Pipeline Name</th>
                    {isSuperAdmin && <th className='fw-bold d-none d-lg-table-cell'>Organization</th>}
                    <th className='fw-bold d-none d-lg-table-cell'>Stages</th>
                    <th className='fw-bold d-none d-lg-table-cell'>Created</th>
                    <th className='fw-bold d-none d-lg-table-cell text-center'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dataArr.map(({node}) => (
                    <tr key={node.id}>
                      <td className='px-4'>{node.name}</td>
                      {isSuperAdmin && (
                        <td className='px-2 d-none d-lg-table-cell'>
                          {node.organization ? node.organization.name : <span className='text-muted'>No organization</span>}
                        </td>
                      )}
                      <td className='px-2 d-none d-lg-table-cell'>
                        <div
                          className='d-flex align-items-center gap-2'
                          style={{
                            whiteSpace: 'nowrap',
                            overflowX: 'auto',
                          }}
                        >
                          {node.stagePipelineSet.edges.length > 0 ? (
                            node.stagePipelineSet.edges
                              .sort((a, b) => a.node.order - b.node.order)
                              .map(({node: nodeStage}, i) => (
                                <span
                                  key={nodeStage.id}
                                  className='d-inline-flex align-items-center gap-2'
                                >
                                  <span className='badge bg-primary text-white'>{nodeStage.name}</span>
                                  {i + 1 !== node.stagePipelineSet.edges.length && (
                                    <i className='bi bi-arrow-right text-dark' />
                                  )}
                                </span>
                              ))
                          ) : (
                            <span className='text-muted'>
                              <i className='bi bi-exclamation-circle' /> No stages
                            </span>
                          )}
                        </div>
                      </td>
                      <td className='px-2 d-none d-lg-table-cell'>
                        {moment(node.created).format('DD MMM, YYYY')}
                      </td>
                      <td className='px-2 text-center d-none d-lg-table-cell'>
                        <div className='d-flex align-items-center justify-content-center gap-2'>
                          <Link to={`/stages/${parseId(node.id)}`} style={{textDecoration: 'none'}}>
                            <Button size='sm' variant='outline-primary'>
                              <i className='bi bi-pencil-square' /> Edit
                            </Button>
                          </Link>
                          <button
                            title='Duplicate Pipeline'
                            style={{border: 0, backgroundColor: 'transparent', padding: '4px 8px'}}
                            onClick={() => {
                              onOpenDuplicate()
                              setInfoEdit({id: node.id, name: node.name})
                            }}
                          >
                            <i className='bi bi-clipboard text-info fs-5' />
                          </button>
                          <button
                            title='Delete Pipeline'
                            style={{border: 0, backgroundColor: 'transparent', padding: '4px 8px'}}
                            onClick={() => {
                              onOpenDeletePipe()
                              setInfoEdit({id: node.id, name: node.name})
                            }}
                          >
                            <i className='bi bi-trash text-danger fs-5' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddPipeline isOpen={isOpen} onClose={onClose} refetch={refetch} infoEdit={infoEdit} />
      )}

      {isOpenDuplicate && infoEdit && (
        <ModalDuplicatePipeline
          isOpen={isOpenDuplicate}
          onClose={onCloseDuplicate}
          refetch={refetch}
          infoEdit={infoEdit}
        />
      )}

      {isOpenDeletePipe && infoEdit && (
        <ModalDeletePipeline
          isOpen={isOpenDeletePipe}
          onClose={onCloseDeletePipe}
          refetch={refetch}
          infoEdit={infoEdit}
        />
      )}
    </div>
  )
}
